import AddIcon from 'assets/icons/add-receiver-outline-icon.svg';
import BusinessIcon from 'assets/icons/business-icon.svg';
import IndividualIcon from 'assets/icons/individual-icon.svg';
import UnidentifiedIcon from 'assets/ver2/icons/receiver-type-unidentified-icon.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import { fetchReceiverInformation } from 'components/Receivers/func';
import {
  BUSINESS_OF_RECEIVER_TYPE,
  DESKTOP_MIN_WIDTH,
  RECEIVERS_ADD_SUFIX,
  RESET_ADD_TRANSACTION_INFORMATION_STORE,
  TRANSACTION_ADD_PREFIX,
} from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { ServiceAlertsByCountryCodeDefault } from 'hooks/service-alerts/model/ServiceAlertsLocalStore';
import useServiceAlertsStore from 'hooks/service-alerts/useServiceAlertsStore';
import useAuth from 'hooks/useAuth';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { SET_ADD_TRANSACTION_INFORMATION } from 'store/action';
import styled from 'styled-components';
import { arrayUtils, domUtils } from 'utils';

const RECEIVERS_SEARCH_NAME = 'receivers-search-field';

const CustomReceiversDropdown = ({
  field,
  receivers,
  information,
  setInformation,
  validation,
  setValidation,
  currentReceiverSelected,
  setCurrentReceiverSelected,
  isDisabled,
  isForceHideDiscountEligibilityToast,
}) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { dispatch } = useStore();

  const query = useQuery();
  const rid = query.get('rid');

  const [searchValue, setSearchValue] = useState('');
  const [receiversSearch, setReceiversSearch] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);
  const displayedReceivers = isSearchInvalid
    ? []
    : receiversSearch || receivers;

  const { serviceAlertsByCountryCode, setServiceAlertsByCountryCode } =
    useServiceAlertsStore();
  const { isResetReceiverDropdown } = serviceAlertsByCountryCode || {};

  useEffect(() => {
    if (isResetReceiverDropdown) {
      setCurrentReceiverSelected(null);

      setServiceAlertsByCountryCode({
        ...ServiceAlertsByCountryCodeDefault,
        isResetReceiverDropdown: false,
      });
    }
  }, [isResetReceiverDropdown]);

  useInputFocus(RECEIVERS_SEARCH_NAME);

  useEffect(() => {
    if (isEmpty(receivers)) return;
    if (isEmpty(rid) && isEmpty(information[field.name])) return;
    const receiver = receivers?.find(
      (c) => c.receiverId === (rid || information[field.name]?.receiverId),
    );
    if (receiver) {
      setCurrentReceiverSelected(receiver);
      const newInformation = {
        ...information,
        [field.name]: receiver,
      };
      setInformation(newInformation);
    } else {
      setCurrentReceiverSelected(null);
    }
  }, [rid, receivers]);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setReceiversSearch(null);

      return;
    }

    const newReceiverSearch = receivers?.filter(
      (receiver) =>
        receiver?.fullname?.toLowerCase()?.includes(value?.toLowerCase()) &&
        receiver,
    );
    setReceiversSearch(newReceiverSearch);
  };

  const handleReceiverSelected = async (receiver) => {
    setSearchValue('');

    setReceiversSearch(null);

    setShowDropdown(false);

    setCurrentReceiverSelected(receiver);

    fetchReceiverInformation(token, receiver?.receiverId);

    const newInformation = {
      ...RESET_ADD_TRANSACTION_INFORMATION_STORE.receiverPayoutMethod,
      [field.name]: receiver,
    };
    setInformation(newInformation);

    const newValidation = {
      ...validation,
      [field.name]: '',
    };
    setValidation(newValidation);

    dispatch({
      type: SET_ADD_TRANSACTION_INFORMATION,
      payload: {
        ...RESET_ADD_TRANSACTION_INFORMATION_STORE,
        receiverPayoutMethod: {
          ...RESET_ADD_TRANSACTION_INFORMATION_STORE.receiverPayoutMethod,
          [field.name]: receiver,
        },
      },
    });

    isForceHideDiscountEligibilityToast.current = false;

    domUtils.changeAddressBarUrlWithoutReloadPage({
      prefix: TRANSACTION_ADD_PREFIX,
    });
  };

  const handleDropdownOnToggle = () => {
    if (isDisabled) {
      setShowDropdown(false);
      return;
    }

    setSearchValue('');
    setReceiversSearch(null);

    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownMenuChildren = () => {
    return (
      <>
        <DropdownSearchInput
          name={RECEIVERS_SEARCH_NAME}
          value={searchValue || ''}
          placeholder={t('form_placeholder_type_a_name_to_search')}
          onChange={handleSearch}
        />
        <DropdownMenuContainer>
          {renderDropdownItems(displayedReceivers)}
        </DropdownMenuContainer>
        <AddButton
          $isReceiversEmpty={arrayUtils.isArrayEmpty(displayedReceivers)}
          $receiverLength={displayedReceivers?.length}
          $isSearchInvalid={isSearchInvalid}
        >
          <ButtonCommon
            value={t('button_add_new_receiver')}
            color="var(--ds-c-blue)"
            background="var(--ds-c-white)"
            styles={{
              paddingInline: 0,
              height: '36px',
              borderRadius: 0,
            }}
            buttonIconStyles={{
              height: '36px',
              flexDirection: 'row-reverse',
              gap: '6px',
            }}
            iconSrc={AddIcon}
            onClick={() => navigate(`${RECEIVERS_ADD_SUFIX}`)}
          />
        </AddButton>
      </>
    );
  };
  const renderDropdownItems = (receiverArray) => {
    if (arrayUtils.isArrayEmpty(receiverArray)) {
      return (
        <DropdownItemStyled eventKey={0} $isEmpty>
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoReceiverText>{t('label_no_receiver')}</NoReceiverText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {receiverArray.map((receiver, index) => {
          const isReceiverTypeUnidentified = isEmpty(receiver?.gender);
          const isReceiverOfBusinessType =
            receiver?.gender === BUSINESS_OF_RECEIVER_TYPE;

          return (
            <DropdownItemStyled
              key={receiver.receiverId}
              data-testid={`receiver-option-${receiver.receiverId}`}
              eventKey={index}
              onClick={() => handleReceiverSelected(receiver)}
            >
              <p className="item-name-country">
                <img
                  src={
                    isReceiverTypeUnidentified
                      ? UnidentifiedIcon
                      : isReceiverOfBusinessType
                        ? BusinessIcon
                        : IndividualIcon
                  }
                  width={24}
                  height={24}
                  style={{ marginRight: '8px' }}
                />
                {`${receiver.fullname} (${receiver.countryName})`}
              </p>
            </DropdownItemStyled>
          );
        })}
      </>
    );
  };

  return (
    <ReceiverDropdownStyled>
      <DropdownCommon
        value={currentReceiverSelected?.fullname}
        placeholder={field?.placeholder}
        keyError={validation?.[field?.name]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        isDisabled={isDisabled}
        dataTestIdDropdownToggle="receiver-picker"
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </ReceiverDropdownStyled>
  );
};

const ReceiverDropdownStyled = styled.div`
  margin-bottom: 32px;
  width: 100%;
  ${Fade};
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
  }
`;

const AddButton = styled.div`
  position: absolute;
  width: 100%;
  background: var(--ds-c-white);
  border-top: 1px solid var(--ds-c-grey-disabled);
  border-radius: 0px 0px 12px 12px;
  margin-top: ${(props) =>
    props.$isReceiversEmpty
      ? props.$isSearchInvalid
        ? '60px'
        : '40px'
      : props.$receiverLength < 4
        ? `${props.$receiverLength * 44}px`
        : '159.4px'};
  box-shadow: var(--ds-bs-3);
  z-index: 1;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-top: ${(props) =>
      props.$isReceiversEmpty
        ? props.$isSearchInvalid
          ? '60px'
          : '40px'
        : props.$receiverLength < 4
          ? `${props.$receiverLength * 44}px`
          : '159.4px'};
  }
`;

const NoReceiverText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: var(--ds-c-grey-hover);
  text-transform: none;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchErrorText = styled(NoReceiverText)`
  color: var(--ds-c-red);
`;

export default CustomReceiversDropdown;
